import { useEffect, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { LBTooltipProvider, LBToaster } from '@linearb/buzz'
import TabNavigation from './components/TabNavigation'
import ROUTES, {
  AUTHENTICATED_ROUTES,
  AUTH_CALLBACK_ROUTE,
  LOGIN_ROUTE,
  MCP_CONSENT_ROUTE
} from './routes'
import useAuthenticate from './hooks/useAuthenticate'
import useUserInfo from './hooks/useUserInfo'
import { useVersionUpdateToast } from './hooks/useVersionCheck'
import { ThemeProvider } from './hooks/useTheme'
import { AiBackfillProvider } from './hooks/useAiBackfill'
import BackfillStatusDock from './components/BackfillStatusDock'
import ChunkErrorBoundary from './components/ChunkErrorBoundary'
import PageSkeleton from './components/PageSkeleton'
import { setRumUser } from './lib/datadogRum'
import { CURRENT_ENVIRONMENT } from './constants/apis'
import {
  pathForEnvironmentSwitch,
  SELECTED_ENVIRONMENT_KEY
} from './constants/environments'

const App: React.FC = () => {
  const { pathname } = useLocation()
  // Nothing on the callback should be reachable while the token exchange runs
  const isSigningIn = pathname === AUTH_CALLBACK_ROUTE.route
  useAuthenticate()
  useVersionUpdateToast()
  const userInfo = useUserInfo()

  useEffect(() => {
    if (userInfo?.email) {
      setRumUser(userInfo.email, userInfo.name)
    }
  }, [userInfo?.email, userInfo?.name])

  // Another tab switched env: this tab's org ids no longer match the API it would load next
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (
        e.key === SELECTED_ENVIRONMENT_KEY &&
        e.newValue !== CURRENT_ENVIRONMENT?.id
      ) {
        window.location.replace(pathForEnvironmentSwitch())
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  useEffect(() => {
    document.documentElement.scrollTop = 0
    document.scrollingElement.scrollTop = 0
  }, [pathname])

  return (
    <ThemeProvider>
      <LBToaster position='bottom-center' />
      <LBTooltipProvider delayDuration={200}>
        <AiBackfillProvider>
          {/* The consent page is a standalone OAuth handoff, like login */}
          {pathname !== LOGIN_ROUTE.route &&
            pathname !== MCP_CONSENT_ROUTE.route && (
              <div inert={isSigningIn}>
                <TabNavigation routes={AUTHENTICATED_ROUTES} />
              </div>
            )}
          <main
            className='pt-16 bg-background min-h-screen'
            inert={isSigningIn}
          >
            <ChunkErrorBoundary>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ROUTES}
                  <Route path='*' element={<Navigate to='/organization' />} />
                </Routes>
              </Suspense>
            </ChunkErrorBoundary>
          </main>
          <BackfillStatusDock />
        </AiBackfillProvider>
      </LBTooltipProvider>
    </ThemeProvider>
  )
}

export default App
